import type { Task } from '../services/taskService';

export type AgentPreference = { enabled: boolean; model_tier: string };

const WEIGHTED_TASK_ROLES = new Set(['UI/UX Designer', 'Frontend Developer', 'Backend Developer', 'AI Engineer', 'DevOps Engineer', 'Full Stack Developer']);
const DEPLOYMENT_TASK_ROLE = 'DevOps Engineer';
const DEPLOYMENT_TASK_BILLING_AGENT_ID = 'smart_command_recovery_agent';
const STAGE_DEPLOYMENT_SRD_REFERENCE = 'Stage deployment';

const WEIGHTED_AGENT_PERCENTAGES_BY_ROLE: Record<string, Record<string, number>> = {
  'UI/UX Designer': {
    implementation_agent: 32.61,
    frontend_quality_agent: 20.56,
    backend_quality_agent: 12.13,
    browser_verification_agent: 14.43,
    branch_preparation_agent: 0.27,
    smart_command_recovery_agent: 20.0,
  },
  'Frontend Developer': {
    implementation_agent: 32.61,
    frontend_quality_agent: 20.56,
    backend_quality_agent: 12.13,
    browser_verification_agent: 14.43,
    branch_preparation_agent: 0.27,
    smart_command_recovery_agent: 20.0,
  },
  'Backend Developer': {
    implementation_agent: 25.88,
    frontend_quality_agent: 21.24,
    browser_verification_agent: 18.82,
    backend_quality_agent: 8.45,
    test_agent: 2.74,
    deployment_phase_6_agent: 2.22,
    branch_preparation_agent: 0.65,
    smart_command_recovery_agent: 20.0,
  },
  'AI Engineer': {
    implementation_agent: 25.88,
    frontend_quality_agent: 21.24,
    browser_verification_agent: 18.82,
    backend_quality_agent: 8.45,
    test_agent: 2.74,
    deployment_phase_6_agent: 2.22,
    branch_preparation_agent: 0.65,
    smart_command_recovery_agent: 20.0,
  },
  'DevOps Engineer': {
    implementation_agent: 25.88,
    frontend_quality_agent: 21.24,
    browser_verification_agent: 18.82,
    backend_quality_agent: 8.45,
    test_agent: 2.74,
    deployment_phase_6_agent: 2.22,
    branch_preparation_agent: 0.65,
    smart_command_recovery_agent: 20.0,
  },
  // Full Stack Developer: midpoint weights between Frontend and Backend Developer.
  'Full Stack Developer': {
    implementation_agent: 29.25,
    frontend_quality_agent: 20.90,
    backend_quality_agent: 10.29,
    browser_verification_agent: 16.63,
    branch_preparation_agent: 0.46,
    smart_command_recovery_agent: 20.0,
    test_agent: 1.37,
    deployment_phase_6_agent: 1.10,
  },
};

const roundCredits = (amount: number): number => Math.round(amount * 100) / 100;

const sumRoleWeights = (roleWeights: Record<string, number>): number => (
  Object.values(roleWeights).reduce((total, weight) => total + weight, 0)
);

export function resolveEffectiveAgentPreferences(
  taskAgents: Record<string, AgentPreference> | undefined,
  projectAgents: Record<string, AgentPreference> | undefined
): Record<string, AgentPreference> {
  if (projectAgents && Object.keys(projectAgents).length > 0) {
    const mergedAgents: Record<string, AgentPreference> = { ...projectAgents };

    if (taskAgents) {
      Object.entries(taskAgents).forEach(([agentId, taskPreference]) => {
        const projectPreference = mergedAgents[agentId];
        if (!projectPreference) return;

        mergedAgents[agentId] = {
          enabled: projectPreference.enabled ? taskPreference.enabled : false,
          model_tier: projectPreference.model_tier,
        };
      });
    }

    return mergedAgents;
  }

  return taskAgents ?? {};
}

export function shouldUseWeightedCalculation(role: string | null | undefined): boolean {
  return !!role && Array.from(WEIGHTED_TASK_ROLES).some((r) => r.toLowerCase() === role!.trim().toLowerCase());
}

export function isDeploymentTask(task: Pick<Task, 'role' | 'title' | 'srd_reference'>): boolean {
  const role = task.role?.trim() ?? '';
  const title = task.title?.trim().toLowerCase() ?? '';
  const srdReference = task.srd_reference?.trim().toLowerCase() ?? '';

  if (srdReference === STAGE_DEPLOYMENT_SRD_REFERENCE.toLowerCase()) return true;

  return (
    role.toLowerCase() === DEPLOYMENT_TASK_ROLE.toLowerCase()
    && title.includes('deploy')
    && (title.includes('stage') || title.includes('deployment'))
  );
}

export function calculateDeploymentTaskCredit(
  agentPreferences: Record<string, AgentPreference>,
  modelMultipliers: Record<string, number>,
  fallbackModelTier: string = 'super_large'
): number {
  const recoveryTier = agentPreferences[DEPLOYMENT_TASK_BILLING_AGENT_ID]?.model_tier ?? fallbackModelTier;

  return roundCredits(modelMultipliers[recoveryTier] ?? modelMultipliers[fallbackModelTier] ?? 10);
}

export function calculateWeightedCredit(
  baseCredit: number,
  role: string | null | undefined,
  agentPreferences: Record<string, AgentPreference>,
  modelMultipliers: Record<string, number>,
  fallbackModelTier: string = 'super_large'
): number {
  const normalizedRole = role?.trim() ?? '';
  const roleWeights = WEIGHTED_AGENT_PERCENTAGES_BY_ROLE[normalizedRole]
    ?? Object.entries(WEIGHTED_AGENT_PERCENTAGES_BY_ROLE).find(([k]) => k.toLowerCase() === normalizedRole.toLowerCase())?.[1]
    ?? {};
  const roleWeightTotal = sumRoleWeights(roleWeights);

  if (!shouldUseWeightedCalculation(role)) {
    const enabledAgents = Object.values(agentPreferences).filter((preference) => preference.enabled);
    if (enabledAgents.length === 0) return 0;

    const maxMultiplier = Math.max(
      ...enabledAgents.map((preference) => modelMultipliers[preference.model_tier] ?? modelMultipliers[fallbackModelTier] ?? 10),
    );
    return roundCredits(baseCredit * maxMultiplier);
  }

  const totalWeightedCost = Object.entries(agentPreferences).reduce((total, [agentId, preference]) => {
    if (!preference.enabled) return total;

    const weight = roleWeights[agentId];
    if (weight === undefined) return total;

    const normalizedWeight = roleWeightTotal > 0 ? (weight / roleWeightTotal) * 100 : weight;
    const multiplier = modelMultipliers[preference.model_tier] ?? modelMultipliers[fallbackModelTier] ?? 10;
    return total + (baseCredit * (normalizedWeight / 100) * multiplier);
  }, 0);

  return roundCredits(totalWeightedCost);
}

export function calculateTaskCredit(
  task: Pick<Task, 'prompt_credit' | 'role' | 'agents' | 'title' | 'srd_reference'>,
  modelMultipliers: Record<string, number>,
  fallbackModelTier: string,
  projectAgents?: Record<string, AgentPreference>
): number {
  const agentPreferences = resolveEffectiveAgentPreferences(task.agents, projectAgents);
  const hasResolvedAgentSettings = Object.keys(agentPreferences).length > 0;

  if (isDeploymentTask(task)) {
    return calculateDeploymentTaskCredit(agentPreferences, modelMultipliers);
  }

  if (hasResolvedAgentSettings) {
    return calculateWeightedCredit(task.prompt_credit, task.role, agentPreferences, modelMultipliers, fallbackModelTier);
  }

  return roundCredits(task.prompt_credit * (modelMultipliers[fallbackModelTier] ?? 10));
}
